import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { isPlanError } from '../../components/plan/planErrors';
import type { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { formatDate, formatRelativeAge } from '../../lib/format';
import type { NewsItemRead, PlannedPostCreate, PlannedPostRead } from '../../types/api';
import { campaignsApi, plannedPostsApi } from '../calendar/calendarApi';
import { monthOf, todayIso } from '../calendar/calendarDates';
import { PlannedPostModal } from '../calendar/PlannedPostModal';
import { useCollection } from '../knowledge-base/useCollection';
import { useAccount } from '../onboarding/useAccount';
import { useBusiness } from '../onboarding/useBusiness';
import { useNewsAutopost } from '../settings/newsAutopostApi';
import { NewsCard } from './NewsCard';
import { NewsEditDialog, DEFAULT_FEED_EDIT_TAB, type FeedEditTab } from './NewsEditDialog';
import { NewsFilterBar } from './NewsFilterBar';
import { NewsFilterPanel } from './NewsFilterPanel';
import { feedCategoriesApi, feedSourcesApi } from './newsApi';
import {
  clearNewsFilterPrefs,
  readNewsFilterPrefs,
  writeNewsFilterPrefs,
  type NewsFilterPrefs,
} from './newsFilterStorage';
import {
  DEFAULT_NEWS_SORT,
  EMPTY_NEWS_FILTER,
  activeFilterCount,
  filterNewsItems,
  isSameFilter,
  newsSourceOptions,
  pruneNewsFilter,
  sortNewsItems,
  sourceCategoryIndex,
  type NewsFilter,
  type NewsSortOrder,
} from './newsFilters';
import { NEWS_SOURCES_ANCHOR } from './newsRoutes';
import { useNews } from './useNews';

/** Stable identity for "nothing fetched yet", so the memos below don't churn. */
const NO_ITEMS: readonly NewsItemRead[] = [];

/**
 * How this business is reading its feed right now, and what it asked to keep.
 *
 * Tagged with the business it belongs to, like a loaded collection: the id
 * arrives a render after the page mounts, and one business's preferences must
 * never be the ones another opens on.
 */
interface NewsPrefsState extends NewsFilterPrefs {
  businessId: string | null;
  /** What is on file for this business on this device, or null when nothing is. */
  saved: NewsFilterPrefs | null;
}

/** The state one business opens on: its saved default, else the whole feed. */
function prefsFor(businessId: string | null): NewsPrefsState {
  const saved = readNewsFilterPrefs(businessId);
  return {
    businessId,
    filter: saved?.filter ?? EMPTY_NEWS_FILTER,
    sort: saved?.sort ?? DEFAULT_NEWS_SORT,
    saved,
  };
}

/**
 * Industry news — what is being said in this business's field, and one step
 * from a headline to a planned post.
 *
 * The screen is the headlines and the two controls over them: an order, and a
 * filter that lives in its own panel. What the feed reads is changed in exactly
 * one other place — the Edit feed dialog, opened from the toolbar, from every
 * "add a source" invitation, and from `/news#sources` — because following,
 * unfollowing and filing are one decision, and a second list of sources on the
 * page behind the dialog was two places to do the same thing.
 *
 * Read-only on the way in: the list is fetched, never generated, so nothing here
 * spends a run. Sorting and filtering rearrange what is already on the client,
 * and the saved default is a local preference, not a record. Two writes, neither
 * of them a pipeline call — a planned post, which is an intention on the
 * calendar exactly like one added there by hand, and a source.
 *
 * Every field of every item is outside text. It is rendered through React's own
 * escaping and never as markup, and a link is only drawn for an http(s) URL.
 */
export function NewsPage() {
  const { businessId, isLoading: isBusinessLoading, error: businessError } = useBusiness();
  // The trade the user named at onboarding. The catalogue opens on the group
  // that matches it, so it travels no further than the dialog.
  const { profile } = useAccount();
  const news = useNews(businessId);
  // The daily autopost, said once in the toolbar so the feature is found
  // where the feed lives; the switch itself is on the settings page.
  const autopost = useNewsAutopost(businessId);
  // The modal offers campaigns; it hides the select entirely when there are none.
  const campaigns = useCollection(campaignsApi, businessId, 'campaigns');
  // The one sources collection on this screen: it names the filter panel's
  // providers and it is what every tab of the Edit feed dialog writes to, so an
  // added source reaches both the same render with no second list to keep in
  // step. Held here rather than in the dialog because the dialog is unmounted
  // when closed and the filter panel is not.
  const sources = useCollection(feedSourcesApi, businessId);
  // The folders over those sources. Held here for the same reason — the dialog
  // comes and goes, and a curation run's category has to survive a close — and
  // now read by the filter panel as well as by the categories tab.
  const categories = useCollection(feedCategoriesApi, businessId);

  // The user's own choices, once they make any. Before that the derived value
  // below is the saved default, read straight out of storage.
  const [held, setHeld] = useState<NewsPrefsState>(() => prefsFor(businessId));
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Null is closed, unless the URL is asking. Opening carries the tab the
  // affordance meant.
  const [editTab, setEditTab] = useState<FeedEditTab | null>(null);
  // Which navigation's `#sources` has already been answered. Without it the
  // hash would reopen the dialog the moment it was closed.
  const [answeredKey, setAnsweredKey] = useState<string | null>(null);
  const [planning, setPlanning] = useState<NewsItemRead | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savePlanError, setSavePlanError] = useState<ApiError | null>(null);
  const [planned, setPlanned] = useState<PlannedPostRead | null>(null);

  const { hash, key: locationKey } = useLocation();

  // Derived during render rather than synced in an effect: until the user
  // changes something, what is in force *is* whatever this business saved, so
  // the id arriving late is enough to apply the right default with no second
  // render and no flash of an unfiltered list.
  const prefs = useMemo(
    () => (held.businessId === businessId ? held : prefsFor(businessId)),
    [held, businessId],
  );

  /**
   * `/news#sources` — what the palette's "Manage news sources" links at. There
   * is nothing on the page to scroll to any more: managing a source *is* the
   * dialog, so the deep link opens it on the tab that lists what is followed.
   *
   * Read during render rather than run as an effect: the hash is one more
   * input to "is the dialog open", not an event to react to. It is keyed on the
   * navigation as well, so following the link again from `/news#sources` — the
   * palette, twice — opens it again, while closing it keeps it closed.
   */
  const isAskedForByUrl = hash === `#${NEWS_SOURCES_ANCHOR}` && answeredKey !== locationKey;
  const openTab: FeedEditTab | null = editTab ?? (isAskedForByUrl ? DEFAULT_FEED_EDIT_TAB : null);

  /** Every "add a source" affordance on the page: open the dialog on tab one. */
  function openFeedEditor() {
    setEditTab(DEFAULT_FEED_EDIT_TAB);
  }

  /**
   * Closing the dialog. A refresh is a real trip out to every feed, so it only
   * runs when the sources actually moved while the dialog was open. An unfollow
   * does not wait for this: it re-pulls on `onSourceRemoved`, the moment the
   * undo window closes, because that delete outlives the dialog.
   */
  function closeFeedEditor(didChange: boolean) {
    setEditTab(null);
    setAnsweredKey(locationKey);
    if (didChange) news.refresh();
  }

  function startPlan(item: NewsItemRead) {
    setSaveError(null);
    setSavePlanError(null);
    setPlanned(null);
    setPlanning(item);
  }

  async function savePost(body: PlannedPostCreate) {
    if (!businessId) return;
    setIsSaving(true);
    setSaveError(null);
    setSavePlanError(null);
    try {
      const created = await plannedPostsApi.create(businessId, body);
      setPlanned(created);
      setPlanning(null);
    } catch (caught) {
      if (isPlanError(caught)) setSavePlanError(caught);
      else setSaveError(getErrorMessage(caught, 'That post did not save.'));
    } finally {
      setIsSaving(false);
    }
  }

  function applyFilter(next: NewsFilter) {
    setHeld({ ...prefs, businessId, filter: next });
  }

  function applySort(next: NewsSortOrder) {
    setHeld({ ...prefs, businessId, sort: next });
  }

  /** "Save as my default" — this filter plus the order in force, on this device. */
  function saveDefault(next: NewsFilter) {
    if (!businessId) return;
    const saved: NewsFilterPrefs = { filter: next, sort: prefs.sort };
    writeNewsFilterPrefs(businessId, saved);
    setHeld({ businessId, filter: next, sort: prefs.sort, saved });
  }

  function clearDefault() {
    if (!businessId) return;
    clearNewsFilterPrefs(businessId);
    setHeld({ ...prefs, businessId, saved: null });
  }

  const items = news.feed?.items ?? NO_ITEMS;

  const sourceOptions = useMemo(
    () => newsSourceOptions(sources.items, items),
    [sources.items, items],
  );
  const categoryBySource = useMemo(() => sourceCategoryIndex(sourceOptions), [sourceOptions]);

  // The filter is held loosely: a provider that was unfollowed and a category
  // that was deleted stop being asked for rather than silently emptying the
  // screen. Only once both lists have really loaded, though — pruning against
  // a collection that has not arrived yet would throw away a saved default.
  const areListsLoaded = !sources.isLoading && !categories.isLoading;
  const activeFilter = useMemo(() => {
    if (!areListsLoaded) return prefs.filter;
    return pruneNewsFilter(
      prefs.filter,
      new Set(sourceOptions.map((option) => option.id)),
      new Set(categories.items.map((category) => category.id)),
    );
  }, [areListsLoaded, prefs.filter, sourceOptions, categories.items]);

  const visibleItems = useMemo(
    () => sortNewsItems(filterNewsItems(items, activeFilter, categoryBySource), prefs.sort),
    [items, activeFilter, categoryBySource, prefs.sort],
  );

  const filterCount = activeFilterCount(activeFilter);
  const isFiltered = filterCount > 0;
  const isDefaultApplied =
    prefs.saved !== null &&
    isSameFilter(activeFilter, prefs.saved.filter) &&
    prefs.sort === prefs.saved.sort;

  return (
    <main className="page page--wide">
      <PageHeader
        section="News"
        title="News"
        lede="What your industry is publishing this week. Reading is free — it doesn’t use a post."
      />

      {isBusinessLoading ? (
        <p className="kb-section__status" role="status">
          Loading your business
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {businessError ??
              'No business is linked to this account yet. Finish setup and the newsfeed opens.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : (
        <section className="news">
          <div className="news-toolbar">
            <p className="news-toolbar__caption">
              {news.feed ? `Updated ${formatRelativeAge(news.feed.fetched_at)}` : 'Not updated yet'}
              {isFiltered ? ` · showing ${visibleItems.length} of ${items.length}` : ''}
              {autopost.settings?.enabled ? (
                <Link className="news-toolbar__pill" to="/settings">
                  Daily autopost on · {autopost.settings.local_time} ·{' '}
                  {autopost.settings.automation === 'auto' ? 'posts itself' : 'for your review'}
                </Link>
              ) : null}
            </p>
            <div className="news-toolbar__actions">
              {/* Pure narrowing and reordering of the list already on screen:
                  no fetch, no run. */}
              <NewsFilterBar
                activeCount={filterCount}
                onOpenFilters={() => setIsFilterOpen(true)}
                sort={prefs.sort}
                onSortChange={applySort}
                isDefaultApplied={isDefaultApplied}
              />
              {/* What the feed reads is changed in one place, opened from the
                  row above the headlines it changes. */}
              <button className="button" type="button" onClick={openFeedEditor}>
                Edit feed
              </button>
              <button
                className="button"
                type="button"
                onClick={news.refresh}
                disabled={news.isLoading || news.isRefreshing}
              >
                {news.isRefreshing ? 'Refreshing' : 'Refresh'}
              </button>
            </div>
          </div>

          {planned ? (
            <p className="news__receipt" role="status">
              Planned “{planned.title}” for {formatDate(planned.scheduled_on)}.{' '}
              <Link to={`/calendar?month=${monthOf(planned.scheduled_on)}`}>View calendar</Link>
            </p>
          ) : null}

          {news.error ? (
            <p className="form__error" role="alert">
              {news.error}
            </p>
          ) : null}

          {saveError && planning === null ? (
            <p className="form__error" role="alert">
              {saveError}
            </p>
          ) : null}

          {news.isLoading ? (
            <p className="kb-section__status" role="status">
              Loading the newsfeed
            </p>
          ) : items.length === 0 ? (
            <div className="kb-section__empty">
              <p className="kb-section__empty-text">
                No industry news found yet — broaden your brand keywords or add a source.
              </p>
              <button className="button" type="button" onClick={openFeedEditor}>
                Add a news source
              </button>
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="kb-section__empty">
              <p className="kb-section__empty-text">Nothing in this update matches that filter.</p>
              <button
                className="button"
                type="button"
                onClick={() => applyFilter(EMPTY_NEWS_FILTER)}
              >
                Show all news
              </button>
            </div>
          ) : (
            <ul className="news-list">
              {/* Two feeds can carry the same article, so the URL alone is not
                  a key. Position is stable here: the list is rebuilt wholesale
                  by the filter and the sort, in the same order for everyone. */}
              {visibleItems.map((item, index) => (
                <NewsCard key={`${index}-${item.url}`} item={item} onPlan={startPlan} />
              ))}
            </ul>
          )}
        </section>
      )}

      {businessId && isFilterOpen ? (
        <NewsFilterPanel
          filter={activeFilter}
          sort={prefs.sort}
          sources={sourceOptions}
          categories={categories.items}
          savedDefault={prefs.saved}
          onApply={applyFilter}
          onSortChange={applySort}
          onSaveDefault={saveDefault}
          onClearDefault={clearDefault}
          onDismiss={() => setIsFilterOpen(false)}
        />
      ) : null}

      {businessId && openTab !== null ? (
        <NewsEditDialog
          businessId={businessId}
          collection={sources}
          categories={categories}
          industry={profile?.industry ?? null}
          initialTab={openTab}
          onSourceRemoved={news.refresh}
          onDismiss={closeFeedEditor}
        />
      ) : null}

      {businessId && planning ? (
        <PlannedPostModal
          businessId={businessId}
          initial={null}
          defaultDate={todayIso()}
          prefill={{
            title: planning.title,
            brief: planning.snippet,
            source_url: planning.url,
          }}
          campaigns={campaigns.items}
          isSaving={isSaving}
          error={saveError}
          planError={savePlanError}
          onSubmit={(body) => void savePost(body)}
          onDismiss={() => setPlanning(null)}
        />
      ) : null}
    </main>
  );
}
